import { Injectable } from '@angular/core';
import { ApiResponse, MiaHttpService } from "@mobileia/core";
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Transaction } from '../entities/transaction';
import { MiaAuthHttpService, AuthenticationService } from '@mobileia/authentication';
import { Office, Delivery } from '../entities/office';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { readSessionCache, writeSessionCache } from '../utils/cache.utils';

@Injectable({
  providedIn: 'root'
})
export class TransactionService extends MiaAuthHttpService {

  private static readonly OFFICES_CACHE_KEY = 'ph_offices_cache';
  private static readonly OFFICES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  transactionCurrent: Transaction;
  currentDelivery = new Subject<Delivery>();
  paymentDiscount = new Subject<number>();
  paymentInstallment = new Subject<any>();
  appliedCoupon = new BehaviorSubject<{ code: string, amount: number } | null>(null);
  email: string;

  private officesCache: ApiResponse<{ offices: Array<Office>, delivery: Array<Delivery> }> | null = null;
  private officesCacheAt = 0;
  private officesRequest$: Observable<ApiResponse<{ offices: Array<Office>, delivery: Array<Delivery> }>> | null = null;

  constructor(
    protected http: HttpClient,
    protected authService: AuthenticationService
  ) {
    super(http, authService);
  }

  createEndpoint(transactionId: number, fingerprint: string, email: string): Observable<ApiResponse<any>> {
    return this.postAuthObject(environment.baseUrl + 'transaction/endpoint', { id: transactionId, fingerprint: fingerprint, email: email });
  }

  list(): Observable<ApiResponse<Array<Transaction>>> {
    return this.postAuthObject(environment.baseUrl + 'transaction/list-user', { });
  }

  listPaymentDiscounts(): Observable<ApiResponse<Array<any>>> {
    return this.postAuthObject(environment.baseUrl + 'payment/discounts', { });
  }

  listPaymentInstallments(): Observable<ApiResponse<Array<any>>> {
    return this.postAuthObject(environment.baseUrl + 'payment/installments', { });
  }

  sendCard(fingerprint: string, transactionId: number, data: any, paymentType: number, couponCode?: string | null): Observable<ApiResponse<any>> {
    const payload: any = { fingerprint: fingerprint, id: transactionId, data: data, type: paymentType };
    if (couponCode && couponCode.trim() !== '') {
      payload.coupon_code = couponCode;
    }
    return this.postAuthObject(environment.baseUrl + 'transaction/send-card', payload);
  }

  generate(fingerprint: string): Observable<ApiResponse<Transaction>> {
    return this.postAuthObject(environment.baseUrl + 'transaction/generate', { fingerprint: fingerprint });
  }

  offices(): Observable<ApiResponse<{ offices: Array<Office>, delivery: Array<Delivery> }>> {
    const now = Date.now();
    this.tryLoadOfficesCache();

    if (this.officesCache && now - this.officesCacheAt < TransactionService.OFFICES_CACHE_TTL_MS) {
      return of(this.officesCache);
    }

    if (this.officesRequest$) {
      return this.officesRequest$;
    }

    this.officesRequest$ = this.postAuthObject(environment.baseUrl + 'office/list', { }).pipe(
      tap(data => {
        if (data && data.success) {
          this.saveOfficesCache(data);
        }
      }),
      finalize(() => this.officesRequest$ = null),
      shareReplay(1)
    );

    return this.officesRequest$;
  }

  calculateOcaShipping(fingerprint: string, zipCode: string): Observable<ApiResponse<any>> {
    return this.postAuthObject(environment.baseUrl + 'shipping/oca-cart', { fingerprint: fingerprint, zip_code: zipCode });
  }

  generateMercadoPagoQR(fingerprint: string, couponCode?: string | null): Observable<ApiResponse<any>> {
    const payload: any = { fingerprint: fingerprint };
    if (couponCode && couponCode.trim() !== '') {
      payload.coupon_code = couponCode;
    }
    return this.postAuthObject(environment.baseUrl + 'payment/mercadopago-qr', payload);
  }

  fetchTransaction(transactionId: number, fingerprint: string, email: string): Observable<ApiResponse<Transaction>> {
    return this.postAuthObject(environment.baseUrl + 'transaction/fetch', { id: transactionId, fingerprint: fingerprint, email: email });
  }

  private tryLoadOfficesCache() {
    if (this.officesCache) {
      return;
    }
    const cached = readSessionCache<ApiResponse<{ offices: Array<Office>, delivery: Array<Delivery> }>>(TransactionService.OFFICES_CACHE_KEY, TransactionService.OFFICES_CACHE_TTL_MS);
    if (cached) {
      this.officesCache = cached.data;
      this.officesCacheAt = cached.timestamp;
    }
  }

  private saveOfficesCache(data: ApiResponse<{ offices: Array<Office>, delivery: Array<Delivery> }>) {
    this.officesCache = data;
    this.officesCacheAt = Date.now();
    writeSessionCache(TransactionService.OFFICES_CACHE_KEY, data, this.officesCacheAt);
  }

}
